import { useEffect, useMemo, useRef, useState } from "react";

import { SearchIcon } from "@/assets/icons";
import LoadingWrapper from "@/components/ui/LoadingWrapper";
import { getVisibleSelectedCount } from "@/helpers/datasetAttributeHelper";
import { useIntersectionObserver } from "@/hooks/ui/useIntersectionObserver";
import { FilterOption } from "@/types/dataset";

import { twMerge } from "tailwind-merge";

const TEST_ID_PREFIX = "multiple-selection-attribute-filter";

type MultipleSelectionFilterProps = {
  options: FilterOption[];
  selectedOptions: string[];
  searchTerm: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectOption: (optionValue: string) => void;
  onSelectAll: (data: FilterOption[]) => void;
  isEdit?: boolean;
  isLoading?: boolean;
};

// Content limit for displaying options
const CONTENT_LIMIT = 100;

const MultipleSelectionFilter = ({
  options,
  selectedOptions,
  searchTerm,
  onSearchChange,
  onSelectOption,
  onSelectAll,
  isEdit = false,
  isLoading = false
}: MultipleSelectionFilterProps) => {
  const [currentLimit, setCurrentLimit] = useState(CONTENT_LIMIT);

  // Search results (no scroll limit) — basis for count & Select All.
  const searchFilteredOptions = useMemo(
    () =>
      options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [options, searchTerm]
  );

  // Sliced for rendering only (infinite scroll).
  const filteredOptions = useMemo(
    () => searchFilteredOptions.slice(0, currentLimit),
    [searchFilteredOptions, currentLimit]
  );

  // Selected count within search results.
  const visibleSelectedCount = useMemo(
    () => getVisibleSelectedCount(selectedOptions, searchFilteredOptions),
    [selectedOptions, searchFilteredOptions]
  );

  const isSelectedAll = useMemo(
    () =>
      searchFilteredOptions.length > 0 &&
      visibleSelectedCount === searchFilteredOptions.length,
    [visibleSelectedCount, searchFilteredOptions.length]
  );

  // lookup for each rendered row's checked state (avoids per-row includes).
  const selectedOptionsSet = useMemo(
    () => new Set(selectedOptions),
    [selectedOptions]
  );

  useEffect(() => {
    // reset the limit when options or searchTerm change
    setCurrentLimit(CONTENT_LIMIT);
  }, [options, searchTerm]);

  const lastItemRef = useRef<HTMLLabelElement | null>(null);
  const isIntersecting = useIntersectionObserver(lastItemRef, {
    threshold: 0.5,
    rootMargin: "10px"
  });

  useEffect(() => {
    if (isIntersecting && currentLimit < searchFilteredOptions.length) {
      setCurrentLimit((prev) => prev + CONTENT_LIMIT);
    }
  }, [currentLimit, isIntersecting, searchFilteredOptions.length]);

  return (
    <LoadingWrapper isLoading={isLoading} loadingClassName="h-full">
      <div className="flex flex-col flex-1 min-h-0 mt-3 font-sans">
        <div className="space-y-3 flex-none">
          <label
            className="text-sm text-txtBody font-semibold"
            htmlFor="search"
          >
            Options
          </label>
          <div className="relative">
            <input
              name="search"
              type="text"
              className="w-full h-[40px] pl-8 pr-3 border border-gainsboro rounded-xl text-xs text-txtBody focus:outline-none focus:border-primary-teal"
              placeholder="Search"
              value={searchTerm}
              onChange={onSearchChange}
              data-testid={`${TEST_ID_PREFIX}-search-input`}
            />
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 fill-light-gray" />
          </div>
        </div>

        <p className="text-xs text-txtBody mt-3">
          Selected options ({visibleSelectedCount} of{" "}
          {searchFilteredOptions.length})
        </p>

        <div className="flex flex-col flex-1 min-h-0 mt-3">
          {searchFilteredOptions.length > 0 && (
            <label className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer flex-none border-b border-gainsboro pb-2 mb-2">
              <input
                type="checkbox"
                className="rounded border-gray-300 w-3.5 h-3.5 accent-primary-teal"
                checked={isSelectedAll}
                onChange={() => onSelectAll(searchFilteredOptions)}
              />
              <span className="text-sm text-txtBody">Select All</span>
            </label>
          )}
          <div
            className={twMerge(
              "overflow-auto flex-1 min-h-0",
              isEdit && "max-h-48"
            )}
          >
            {filteredOptions.length > 0 ? (
              <>
                {filteredOptions.map((option, index) => (
                  <label
                    key={option.id}
                    ref={
                      index === filteredOptions.length - 1 ? lastItemRef : null
                    }
                    className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 w-3.5 h-3.5 accent-primary-teal"
                      checked={selectedOptionsSet.has(option.value ?? "")}
                      onChange={() =>
                        option.value && onSelectOption(option.value)
                      }
                    />
                    <span className="text-sm text-txtBody">{option.label}</span>
                  </label>
                ))}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center p-4">
                <SearchIcon className="w-6 h-6 fill-txtPlaceholder mb-2" />

                <div className="text-txtPlaceholder mb-2 font-semibold">
                  Options Not Found
                </div>
                <div className="text-sm text-txtPlaceholder">
                  Please try again with different keywords.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </LoadingWrapper>
  );
};

export default MultipleSelectionFilter;
